"use client"

import Slider from "@/components/ui/slider";
import Link from "next/link";
import Image from "next/image";
import {BookOpen, ChevronLeft, MessageSquare, X} from "lucide-react"; // Eye
import { formatRelativeDate } from "@/lib/date";
import {useEffect, useState} from "react";
import {ProductImage} from "@/components/ui/ProductImage";
import {strLimit , truncateHtml} from "@/lib/helper";
import {PostImage} from "@/components/ui/PostImage";
import {whyChooseItems,} from "@/components/data/WhyChoose";
import type { Props } from "@/types/home";

const categoryColors = [
    "#edf3ff",
    "#fff3f5",
    "#edfbf7",
    "#fff6ec",
    "#e6f9f5",
    "#edf1f6",
    "#f3e6ff",
];

export default function HomePage({ data, categoriesMother }: Props) {

    const categories = data.categories ?? [];
    const topics = data.topic ?? [];
    // const products = data.most_viewed_products ?? [];
    const blogs = data.last_blog ?? [];

    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (isOpen) {
            document.body.classList.add("overflow-hidden");
        } else {
            document.body.classList.remove("overflow-hidden");
        }

        return () => {
            document.body.classList.remove("overflow-hidden");
        };
    }, [isOpen]);

    const visibleCategories = categoriesMother.slice(0, 9);
    const popupCategories = categoriesMother.slice(0);

    return (
        <main className="space-y-12">

            {/* Main Categories */}
            <section className="relative bg-blue-900 dark:bg-blue-900/20">
                <div className={'container !py-6'}>
                    <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-10 gap-2">
                        {visibleCategories.map((item) => (
                            item.primary_image && (
                                <Link
                                    key={item.id}
                                    href={`/${process.env.NEXT_PUBLIC_SHOP_CATEGORY}/${item.slug}`}
                                    className="flex flex-col items-center text-center transition-all duration-300 ease-out hover:scale-105 group"
                                    prefetch={true}
                                >
                                    <div className="w-20 h-20 flex items-center justify-center rounded-xl relative bg-gray-200 transition-all duration-300 group-hover:bg-gray-300/90 group-hover:shadow-md dark:bg-gray-500">
                                        <Image
                                            className="object-contain p-3"
                                            src={`${process.env.NEXT_PUBLIC_STORAGE_UPLOADS_ASSETS_IMG}/menu/${item.primary_image}`}
                                            alt={'تصویر ' + item.title}
                                            fill
                                            sizes="80px"
                                        />
                                    </div>
                                    <span className="mt-3 text-sm font-medium text-white group-hover:text-gray-300">
                                        {item.title}
                                    </span>
                                </Link>
                            )
                        ))}

                        {popupCategories.length > 0 && (
                            <button
                                onClick={() => setIsOpen(true)}
                                className="flex flex-col items-center text-center transition-all duration-300 ease-out hover:scale-105 group cursor-pointer"
                            >
                                <div className="w-20 h-20 flex items-center justify-center text-3xl rounded-full bg-gray-200 transition-all duration-300 group-hover:bg-gray-300/60 group-hover:shadow-md">
                                    ...
                                </div>
                                <span className="mt-3 text-sm font-medium text-white group-hover:text-gray-300">
                                    نمایش بیشتر
                                </span>
                            </button>
                        )}
                    </div>
                </div>
            </section>

            <div className={'container no-padd-y'}>

                {/* Forum Topics */}
                <section className="rounded-lg bg-white shadow-sm p-5 mb-3">
                    {/* HEADER */}
                    <div className="flex items-center justify-between mb-5">
                        <h2 className="flex items-center gap-3 text-xl font-bold text-gray-900">
                            <div
                                className="w-10 h-10 bg-gray-200/50 backdrop-blur-sm rounded-lg flex items-center justify-center">
                                    <MessageSquare className="w-8 h-8 text-blue-600 dark:text-white"/>
                                </div>
                            <span>
                              انجمن و تاپیک
                            </span>
                        </h2>
                        <Link
                            href={`/${process.env.NEXT_PUBLIC_FORUM_MAIN}`}
                            className="flex items-center gap-2 rounded-lg bg-gray-100 hover:bg-gray-200 px-4 py-2 transition"
                            aria-label="مشاهده همه مطالب انجمن"
                            title={"مشاهده همه مطالب انجمن"}
                            prefetch={true}
                        >
                            <span className="text-sm font-medium hidden sm:block">
                                مشاهده همه
                            </span>
                            <ChevronLeft className="w-4 h-4" />
                        </Link>
                    </div>
                    {/* Content */}
                    <div className="grid gap-2 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                        {topics.map((item) => (
                            <Link
                                key={item.id}
                                href={`/${process.env.NEXT_PUBLIC_FORUM_TOPIC}/topic-${item.id}`}
                                className="rounded-xl border border-gray-200 p-4 hover:bg-gray-100/50 flex flex-col"
                                prefetch={true}
                            >
                                <div>
                                    <h3 className="mb-2 font-bold">
                                        {item.title}
                                    </h3>

                                    <div
                                        className="line-clamp-2 text-sm text-gray-600"
                                        dangerouslySetInnerHTML={{
                                            __html: truncateHtml(
                                                item.description_short,
                                                120
                                            ),
                                        }}
                                    />
                                </div>

                                <div className="flex items-center justify-between gap-3 text-sm text-gray-500 mt-auto">
                                    <span>{formatRelativeDate(item.created_at)}</span>
                                    <span>{item.author.info?.name ?? item.author.username }</span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>

                {/* Why us */}
                <section className="section-padding-0 p-0 m-0 mb-3">
                    <div className="container no-padd">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                            {whyChooseItems.map((item) => (
                                <div
                                    key={item.id}
                                    className="rounded-lg bg-white shadow-sm flex flex-col border-0 overflow-hidden h-full transition p-5"
                                >
                                    <div className="flex items-center justify-center pb-0">
                                        <div
                                            className={`w-16 h-16 rounded-full bg-gradient-to-br ${item.gradient} flex items-center justify-center`}
                                        >
                                            <svg
                                                className="w-8 h-8 text-white"
                                                fill="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                {item.icon}
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="text-center flex flex-col flex-1">
                                        <div className="text-lg font-bold text-gray-900 mt-5 mb-2">
                                            {item.title}
                                        </div>

                                        <div className="text-sm text-gray-600 leading-relaxed">
                                            {item.description}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Most Viewed Products */}
                {/*<section className="rounded-lg bg-white shadow-sm p-2 mb-3">*/}
                {/*    /!* HEADER *!/*/}
                {/*    <div className="flex items-center justify-between mb-5 p-2 pb-0">*/}
                {/*        <h2 className="flex items-center gap-3 text-xl font-bold text-gray-900">*/}
                {/*            <div*/}
                {/*                className="w-10 h-10 bg-gray-200/50 backdrop-blur-sm rounded-lg flex items-center justify-center">*/}
                {/*                <Eye className="w-8 h-8 text-red-600"/>*/}
                {/*            </div>*/}
                {/*            <span>*/}
                {/*               پربازدیدترین محصولات*/}
                {/*            </span>*/}
                {/*        </h2>*/}
                {/*        <Link*/}
                {/*            href={ "/" + process.env.NEXT_PUBLIC_SHOP_MAIN }*/}
                {/*            className="flex items-center gap-2 rounded-lg bg-gray-100 hover:bg-gray-200 px-4 py-2 transition"*/}
                {/*            aria-label="مشاهده همه محصولات"*/}
                {/*            title={'مشاهده همه محصولات'}*/}
                {/*            prefetch={true}*/}
                {/*        >*/}
                {/*            <span className="text-sm font-medium hidden sm:block">*/}
                {/*                مشاهده همه*/}
                {/*            </span>*/}
                {/*            <ChevronLeft className="w-4 h-4" />*/}
                {/*        </Link>*/}
                {/*    </div>*/}
                {/*    /!* Content *!/*/}
                {/*    <Slider items={5}>*/}
                {/*        {products.map((item) => (*/}
                {/*            <Link*/}
                {/*                title={item.name}*/}
                {/*                key={item.id}*/}
                {/*                href={`/${process.env.NEXT_PUBLIC_SHOP_PRODUCT_SINGLE}/${item.slug}`}*/}
                {/*                className="w-full border border-gray-200 rounded-lg hover:border-blue-600 p-2 h-full grid grid-rows-[auto_1fr_auto] gap-2"*/}
                {/*                prefetch={true}*/}
                {/*            >*/}
                {/*                <ProductImage*/}
                {/*                    src={*/}
                {/*                        item.primary_image*/}
                {/*                            ? `${process.env.NEXT_PUBLIC_STORAGE_UPLOADS_PRODUCT}/${item.primary_image.split(",")[0]}.${process.env.NEXT_PUBLIC_STORAGE_EXTENSION_PICTURE}?w=200`*/}
                {/*                            : `${process.env.NEXT_PUBLIC_STORAGE_UPLOADS_ASSETS_IMG}/lazy/load.webp`*/}
                {/*                    }*/}
                {/*                    alt={`نمونه تصویر ${strLimit(item.name)}`}*/}
                {/*                />*/}
                {/*                <h3 className="line-clamp-3 text-lg font-medium h-[80px]">*/}
                {/*                    {item.name}*/}
                {/*                </h3>*/}
                {/*                <p className="text-sm text-gray-700 text-left ltr">*/}
                {/*                    P/N: {item.sku}*/}
                {/*                </p>*/}
                {/*            </Link>*/}
                {/*        ))}*/}
                {/*    </Slider>*/}
                {/*</section>*/}

                {/* Categories Products */}
                {categories.map((category, index) => (
                    <section key={category.id} className="rounded-lg bg-white shadow-sm p-2 mb-3">
                        {/* HEADER */}
                        <div className="flex items-center justify-between mb-5 p-2 pb-0">
                            <h2 className="flex items-center gap-3 text-xl font-bold text-gray-900">
                                <div
                                    className="w-10 h-10 p-0.5 flex items-center justify-center bg-gray-200/50 backdrop-blur-sm rounded-lg"

                                    style={{
                                        backgroundColor: categoryColors[index % categoryColors.length],
                                    }}
                                >
                                    <Image
                                        src={
                                            category.primary_image
                                                ? `${process.env.NEXT_PUBLIC_STORAGE_UPLOADS_ASSETS_IMG}/menu/${category.primary_image}`
                                                : `${process.env.NEXT_PUBLIC_STORAGE_UPLOADS_ASSETS_IMG}/lazy/load.webp`
                                        }
                                        alt={category.title}
                                        width={50}
                                        height={50}
                                        className="object-contain"
                                    />
                                </div>
                                <span>
                                {category.pin_title}
                            </span>
                            </h2>
                            <Link
                                href={`/${process.env.NEXT_PUBLIC_SHOP_CATEGORY}/${category.slug}`}
                                className="flex items-center gap-2 rounded-lg bg-gray-100 hover:bg-gray-200 px-4 py-2 transition"
                                aria-label={'مشاهده همه ' + category.pin_title}
                                title={'مشاهده همه ' + category.pin_title}
                                prefetch={true}
                            >
                            <span className="text-sm font-medium hidden sm:block">
                                مشاهده همه
                            </span>
                                <ChevronLeft className="w-4 h-4" />
                            </Link>
                        </div>
                        {/* Content */}

                        {/* Content */}
                        <Slider items={5}>
                            {category.data?.map((product) => (
                                <Link
                                    title={product.name}
                                    key={product.id}
                                    href={`/${process.env.NEXT_PUBLIC_SHOP_PRODUCT_SINGLE}/${product.slug}`}
                                    className="w-full border border-gray-200 rounded-lg hover:border-blue-600 p-2 h-full grid grid-rows-[auto_1fr_auto] gap-2"
                                    prefetch={true}
                                >
                                    <ProductImage
                                        src={
                                            product.primary_image
                                                ? `${process.env.NEXT_PUBLIC_STORAGE_UPLOADS_PRODUCT}/${product.primary_image.split(",")[0]}.${process.env.NEXT_PUBLIC_STORAGE_EXTENSION_PICTURE}?w=200`
                                                : `${process.env.NEXT_PUBLIC_STORAGE_UPLOADS_ASSETS_IMG}/lazy/load.webp`
                                        }
                                        alt={`نمونه تصویر ${strLimit(product.name)}`}
                                    />
                                    <h3 className="line-clamp-3 text-lg font-medium h-[80px]">
                                        {product.name}
                                    </h3>
                                    <p className="text-sm text-gray-700 text-left ltr uppercase">
                                        P/N: {product.sku}
                                    </p>
                                </Link>
                            ))}
                        </Slider>
                    </section>
                ))}

                {/* Blogs */}
                <section className="rounded-lg bg-white shadow-sm p-2 mb-3">
                    {/* HEADER */}
                    <div className="flex items-center justify-between mb-5 p-2 pb-0">
                        <h2 className="flex items-center gap-3 text-xl font-bold text-gray-900">
                            <div
                                className="w-10 h-10 bg-gray-200/50 backdrop-blur-sm rounded-lg flex items-center justify-center">
                                <BookOpen className="w-8 h-8 text-red-600 dark:text-white"/>
                            </div>
                            <span>
                               آخرین مقالات
                            </span>
                        </h2>
                        <Link
                            href={ "/" + process.env.NEXT_PUBLIC_BLOG_MAIN }
                            className="flex items-center gap-2 rounded-lg bg-gray-100 hover:bg-gray-200 px-4 py-2 transition"
                            aria-label="مشاهده همه مقالات"
                            title={'مشاهده همه مقالات'}
                            prefetch={true}
                        >
                            <span className="text-sm font-medium hidden sm:block">
                                مشاهده همه
                            </span>
                            <ChevronLeft className="w-4 h-4" />
                        </Link>
                    </div>
                    {/* Content */}
                    <Slider items={4}>
                        {blogs.map((item) => (
                            <Link
                                title={item.title}
                                key={item.id}
                                href={`/${process.env.NEXT_PUBLIC_BLOG_POST_SINGLE}/${item.slug}`}
                                className="w-full border border-gray-200 rounded-lg hover:border-blue-600 p-2 h-full grid grid-rows-[auto_1fr_auto] gap-2"
                                prefetch={true}
                            >
                                <PostImage
                                    src={
                                        item.primary_image
                                            ? `${process.env.NEXT_PUBLIC_STORAGE_UPLOADS_POST}/${item.primary_image.split(",")[0]}.${process.env.NEXT_PUBLIC_STORAGE_EXTENSION_PICTURE}?w=300`
                                            : `${process.env.NEXT_PUBLIC_STORAGE_UPLOADS_ASSETS_IMG}/lazy/load-post.webp?w=200`
                                    }
                                    alt={`نمونه تصویر ${strLimit(item.title)}`}
                                />
                                <h3 className="text-lg font-medium">
                                    {item.title}
                                </h3>
                            </Link>
                        ))}
                    </Slider>
                </section>
            </div>

            {/* Popup Category */}
            {isOpen && popupCategories.length > 0 && (
                <div
                    className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
                    onClick={(e) => e.target === e.currentTarget && setIsOpen(false)}
                >
                    <div className="bg-white z-50 rounded-xl w-11/12 md:w-2/3 p-8 relative animate-slide-up max-h-[80vh] overflow-y-auto">
                        <button
                            onClick={() => setIsOpen(false)}
                            className="absolute top-4 left-4 text-gray-500 hover:bg-gray-300 cursor-pointer text-xl font-bold w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center"
                        >
                            <X className={'w-6 h-6'} />
                        </button>
                        <h3 className="text-xl font-bold mb-8 text-center border-b border-gray-200 pb-4">
                            دسته‌بندی‌ها
                        </h3>
                        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-6 text-center">
                            {popupCategories.map((cat) => (
                                <Link
                                    key={cat.id}
                                    href={`/${process.env.NEXT_PUBLIC_SHOP_CATEGORY}/${cat.slug}`}
                                    className="flex flex-col items-center text-center transition-all duration-300 ease-out hover:scale-105 group"
                                    prefetch={true}
                                >
                                    <div className="w-20 h-20 flex relative items-center justify-center rounded-full bg-gray-200 transition-all duration-300 group-hover:bg-gray-300/60 group-hover:shadow-md">
                                        <Image
                                            className="object-contain p-3"
                                            src={
                                                cat.primary_image
                                                    ? `${process.env.NEXT_PUBLIC_STORAGE_UPLOADS_ASSETS_IMG}/menu/${cat.primary_image}`
                                                    : `${process.env.NEXT_PUBLIC_STORAGE_UPLOADS_ASSETS_IMG}/lazy/load.webp`
                                            }
                                            alt={'تصویر ' + cat.title}
                                            fill
                                            sizes="80px"
                                        />
                                    </div>
                                    <span className="mt-3 text-sm font-medium text-gray-700 group-hover:text-black">
                                        {cat.title}
                                    </span>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            )}

        </main>
    );
}